import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { useEffect, useState } from "react";
import {
  getActiveDriversLocations,
  getCustomersWithPendingOrders,
} from "../../services/location.service";
import AssignDriverModal from "../order/AssignDriverModal";
import { acceptOrder } from "../../services/order.service";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "./DriversMap.css";
import L from "leaflet";
import driverIcon from "../../assets/markers/driver-active.png";
import customerIcon from "../../assets/markers/customer.png";
import { useToast } from "../../context/ToastContext";

const DriverIcon = L.icon({
  iconUrl: driverIcon,
  iconSize: [56, 56],
  iconAnchor: [28, 56],
  popupAnchor: [0, -48],
});

const CustomerIcon = L.icon({
  iconUrl: customerIcon,
  iconSize: [60, 60],
  iconAnchor: [28, 50],
});

function MapControls({ locations, onReload, onFullscreen }) {
  const map = useMap();

  function recenter() {
    if (!locations.length) return;

    const bounds = L.latLngBounds(
      locations.map((loc) => [loc.latitude, loc.longitude]),
    );

    map.fitBounds(bounds, {
      padding: [80, 80],
      maxZoom: 16,
      animate: true,
    });
  }

  return (
    <div className="mapControls">
      <button onClick={recenter}>🎯</button>
      <button onClick={onReload}>🔄</button>
      <button onClick={onFullscreen}>
        {document.fullscreenElement ? "🡼" : "⛶"}
      </button>
    </div>
  );
}
function MapResizer({ fullscreen }) {
  const map = useMap();

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 300);
  }, [fullscreen, map]);

  return null;
}
function AutoFit({ locations }) {
  const map = useMap();

  useEffect(() => {
    if (locations.length === 0) return;

    const bounds = L.latLngBounds(
      locations.map((loc) => [loc.latitude , loc.longitude]),
    );

    map.fitBounds(bounds, {
      padding: [80, 80],
      maxZoom: 16,
      animate: true,
    });
  }, [locations, map]);

  return null;
}
const AnimatedDriverIcon = L.divIcon({
  className: "driverMarker",
  html: `
    <div class="pulseMarker">
      <img src="${driverIcon}" />
    </div>
  `,
  iconSize: [60, 60],
  iconAnchor: [30, 30],
  popupAnchor: [0, -30],
});
const AnimatedCustomerIcon = L.divIcon({
  className: "customerMarker",
  html: `
    <div class="pulseCustomer">
      <img src="${customerIcon}" />
    </div>
  `,
  iconSize: [50, 50],
  iconAnchor: [25, 25],
  popupAnchor: [0, -30],
});

export default function DriversMap() {
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  async function loadLocations() {
    setLoading(true);

    const [driversData, customersData] = await Promise.all([
      getActiveDriversLocations(),
      getCustomersWithPendingOrders(),
    ]);

    setDrivers(driversData);
    setCustomers(customersData);

    setLoading(false);
  }

  useEffect(() => {
    loadLocations();
  }, []);

  if (drivers.length === 0 && customers.length === 0 && !loading) {
    return (
      <div className={`mapCard ${fullscreen ? "fullscreen" : ""}`}>
        <div className="mapHeader">
          <h3>Position des chauffeurs</h3>
          <span className="mapCount">0 en ligne</span>
        </div>
        <div className="mapEmpty">Aucun chauffeur actif actuellement</div>
      </div>
    );
  }
  function openAssignModal(customerLocation) {
    setSelectedOrder(customerLocation);
    setShowAssignModal(true);
  }
  async function assignDriver(location, driverId) {
    const orderId = location.user.customer.orders[0].id;

    await acceptOrder(orderId, driverId);

    setShowAssignModal(false);
    loadLocations();
    showToast("Chauffeur assigné avec success", "success");
  }
  const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });

  L.Marker.prototype.options.icon = DefaultIcon;
  const allLocations = [...drivers, ...customers];
  return (
    <div className={`mapCard ${fullscreen ? "fullscreen" : ""}`}>
      <div className="mapHeader">
        <h3>Position des chauffeurs</h3>
        <span className="mapCount">
          🟢 {drivers.length} chauffeurs | 🟠 {customers.length} commandes
        </span>
      </div>

      <div className="mapContainer">
        <MapContainer
          zoom={13}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%" }}
        >
          <MapResizer fullscreen={fullscreen} />
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <AutoFit locations={allLocations} />
          <MapControls
            locations={allLocations}
            onReload={loadLocations}
            onFullscreen={() => setFullscreen((v) => !v)}
          />
          {drivers.map((loc) => (
            <Marker
              key={loc.id}
              position={[loc.latitude,loc.longitude]}
              icon={AnimatedDriverIcon}
            >
              <Popup className="driverPopup">
                <div className="driverCard">
                  <div className="driverName">
                    {loc.user?.firstName} {loc.user?.lastName}
                  </div>

                  <div className="driverPhone">📞 {loc.user?.contact?.tel}</div>

                  <div className="driverStatus">Chauffeur disponible</div>
                </div>
              </Popup>
            </Marker>
          ))}
          {customers.map((loc) => (
            <>
              <Marker
                key={loc.id}
                position={[ loc.latitude,loc.longitude]}
                icon={AnimatedCustomerIcon}
              >
                <Popup className="orderPopup">
                  <div className="orderCard">
                    <div className="orderHeader">
                      <span className="orderClient">
                        {loc.user?.firstName} {loc.user?.lastName}
                      </span>

                      <span className="orderStatus">Commande en attente</span>
                    </div>

                    <div className="orderInfo">
                      <div>📞 {loc.user?.contact?.tel}</div>
                      <div>📍 Client localisé</div>
                    </div>

                    <div className="orderActions">
                      <button
                        className="assignBtn"
                        onClick={() => openAssignModal(loc)}
                      >
                        Assigner chauffeur
                      </button>

                      <button
                        className="focusBtn"
                        onClick={() =>
                          window.open(
                            `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`,
                          )
                        }
                      >
                        Voir sur maps
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            </>
          ))}
        </MapContainer>
        {showAssignModal && (
          <AssignDriverModal
            order={selectedOrder}
            onAssign={assignDriver}
            onClose={() => setShowAssignModal(false)}
          />
        )}
      </div>
    </div>
  );
}
